import { useEffect, useState, type FormEvent } from "react";
import type { CatalogItemData, CatalogItemFields } from "@contracts/index";
import { ErrorBox } from "../../components/AdminLayout";
import { api, errorMessage } from "../../lib/api";
import { centsToInput, COMMON_CURRENCIES, formatMoney, parseMoneyToCents } from "../../lib/format";

interface CatalogForm {
  concept: string;
  description: string;
  active: boolean;
  /** Price text per currency code; empty means "no price in this currency". */
  prices: Record<string, string>;
}

const EDITOR_CURRENCIES = ["MXN", "USD", "EUR"];

function emptyForm(): CatalogForm {
  return { concept: "", description: "", active: true, prices: {} };
}

function toForm(item: CatalogItemData): CatalogForm {
  return {
    concept: item.concept,
    description: item.description,
    active: item.active,
    prices: Object.fromEntries(Object.entries(item.prices).map(([c, cents]) => [c, centsToInput(cents)])),
  };
}

function toFields(form: CatalogForm): CatalogItemFields {
  const prices: Record<string, number> = {};
  for (const [currency, text] of Object.entries(form.prices)) {
    if (!text.trim()) continue;
    prices[currency] = parseMoneyToCents(text) ?? Number.NaN;
  }
  return { concept: form.concept, description: form.description, active: form.active, prices };
}

/** Reusable concepts with a price per currency, to fill quotes faster. */
export function CatalogPage() {
  const [items, setItems] = useState<CatalogItemData[]>([]);
  const [editing, setEditing] = useState<string | "new" | null>(null);
  const [form, setForm] = useState<CatalogForm>(emptyForm);
  const [extraCurrency, setExtraCurrency] = useState("");
  const [error, setError] = useState<string | null>(null);

  const load = () => api.admin.listCatalog().then(setItems).catch((e: unknown) => setError(errorMessage(e)));
  useEffect(() => {
    void load();
  }, []);

  function startEdit(item?: CatalogItemData) {
    setError(null);
    setEditing(item ? item.id : "new");
    setForm(item ? toForm(item) : emptyForm());
  }

  async function onSubmit(event: FormEvent) {
    event.preventDefault();
    setError(null);
    try {
      const fields = toFields(form);
      if (editing === "new") await api.admin.createCatalogItem(fields);
      else if (editing) await api.admin.updateCatalogItem(editing, fields);
      setEditing(null);
      await load();
    } catch (e) {
      setError(errorMessage(e));
    }
  }

  const currencies = Array.from(new Set([...EDITOR_CURRENCIES, ...Object.keys(form.prices)]));

  return (
    <section className="card">
      <div className="row">
        <h1 className="grow">Catálogo</h1>
        <button className="primary" onClick={() => startEdit()}>
          Nuevo concepto
        </button>
      </div>
      <ErrorBox message={error} />

      {editing && (
        <form className="card inset" onSubmit={onSubmit}>
          <label>
            Concepto
            <input required value={form.concept} onChange={(e) => setForm({ ...form, concept: e.target.value })} />
          </label>
          <label>
            Descripción
            <textarea rows={3} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} />
          </label>
          <div className="grid">
            {currencies.map((currency) => (
              <label key={currency}>
                Precio {currency}
                <input
                  inputMode="decimal"
                  value={form.prices[currency] ?? ""}
                  onChange={(e) => setForm({ ...form, prices: { ...form.prices, [currency]: e.target.value } })}
                />
              </label>
            ))}
          </div>
          <div className="row">
            <input
              list="catalog-currencies"
              placeholder="Otra moneda (ej. GTQ)"
              maxLength={3}
              value={extraCurrency}
              onChange={(e) => setExtraCurrency(e.target.value.toUpperCase())}
            />
            <datalist id="catalog-currencies">
              {COMMON_CURRENCIES.map((c) => (
                <option key={c} value={c} />
              ))}
            </datalist>
            <button
              type="button"
              className="secondary"
              onClick={() => {
                if (/^[A-Z]{3}$/.test(extraCurrency)) {
                  setForm({ ...form, prices: { ...form.prices, [extraCurrency]: form.prices[extraCurrency] ?? "" } });
                  setExtraCurrency("");
                }
              }}
            >
              Agregar moneda
            </button>
          </div>
          <label className="inline">
            <input type="checkbox" checked={form.active} onChange={(e) => setForm({ ...form, active: e.target.checked })} />
            Activo (aparece al crear cotizaciones)
          </label>
          <div className="actions">
            <button type="button" className="secondary" onClick={() => setEditing(null)}>
              Cancelar
            </button>
            <button className="primary" type="submit">
              Guardar
            </button>
          </div>
        </form>
      )}

      {items.length === 0 ? (
        <p className="muted">Aún no hay conceptos en el catálogo.</p>
      ) : (
        <div className="table-wrap">
          <table>
            <thead>
              <tr>
                <th>Concepto</th>
                <th>Precios</th>
                <th>Estado</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {items.map((item) => (
                <tr key={item.id} className={item.active ? "" : "muted"}>
                  <td>
                    {item.concept}
                    {item.description && <div className="muted small">{item.description}</div>}
                  </td>
                  <td>
                    {Object.entries(item.prices)
                      .map(([c, cents]) => formatMoney(cents, c))
                      .join(" · ")}
                  </td>
                  <td>{item.active ? "Activo" : "Inactivo"}</td>
                  <td>
                    <button className="link" onClick={() => startEdit(item)}>
                      Editar
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
}
